import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Errore imprevisto";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
}

export default function UserLogin({ mode = "login" }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nome, setNome] = useState("");
  const [busy, setBusy] = useState(false);
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Compila email e password"); return; }
    if (isRegister && !nome.trim()) { toast.error("Inserisci il tuo nome"); return; }
    setBusy(true);
    try {
      const url = isRegister ? "/user-auth/register" : "/user-auth/login";
      const body = isRegister ? { email, password, nome } : { email, password };
      const r = await axios.post(`${API}${url}`, body);
      localStorage.setItem("cc_user_token", r.data.access_token);
      localStorage.setItem("cc_user_nome", r.data.nome || "");
      localStorage.setItem("cc_user_email", r.data.email || email);
      localStorage.setItem("cc_user_account_type", r.data.account_type || "locatore");
      if (r.data.agenzia_nome) localStorage.setItem("cc_user_agenzia_nome", r.data.agenzia_nome);
      // Show a one-time (per-login) informational banner in the dashboard
      // reminding the user to logout/login if freshly-linked practices are
      // not visible yet. Dismissible; resets on next login.
      sessionStorage.setItem("cc_user_show_login_notice", "1");
      toast.success(isRegister ? "Account creato" : "Bentornato");
      // Agency accounts belong to the dedicated Agenzia panel.
      if ((r.data.account_type || "").toLowerCase() === "agenzia") {
        nav("/agenzia/dashboard");
      } else {
        nav("/utente");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid={`user-${mode}-page`}>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-8">
        <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Unioncasa · Pannello Associato</p>
        <h1 className="text-2xl font-serif text-slate-900 mb-6">{isRegister ? "Crea il tuo account" : "Accedi al tuo pannello"}</h1>
        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Nome e cognome</label>
              <input type="text" value={nome} onChange={e=>setNome(e.target.value)}
                className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-slate-900 text-sm"
                data-testid="user-nome-input" required />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-slate-900 text-sm"
              data-testid="user-email-input" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Password</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:border-slate-900 text-sm"
              data-testid="user-password-input" required minLength={isRegister ? 8 : 1} />
            {isRegister && <p className="text-[11px] text-slate-500 mt-1">Minimo 8 caratteri.</p>}
          </div>
          <button type="submit" disabled={busy}
            className="w-full h-11 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-60"
            data-testid="user-submit-btn">
            {busy ? "Attendere..." : (isRegister ? "Crea account" : "Accedi")}
          </button>
        </form>
        {!isRegister && (
          <p className="text-xs text-slate-500 mt-3 text-right">
            <Link to="/utente/password-dimenticata" className="hover:text-slate-900 hover:underline" data-testid="user-forgot-password-link">
              Password dimenticata?
            </Link>
          </p>
        )}
        <p className="text-xs text-slate-600 mt-6 text-center">
          {isRegister ? (
            <>Hai già un account? <Link to="/utente/login" className="text-slate-900 font-medium underline">Accedi</Link></>
          ) : (
            <>Non hai un account? <Link to="/utente/register" className="text-slate-900 font-medium underline">Registrati</Link></>
          )}
        </p>
        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Le pratiche già inviate con questa email verranno automaticamente collegate al tuo account.
        </p>
        <p className="text-[11px] text-slate-500 mt-3 text-center border-t border-slate-100 pt-3">
          Sei un professionista incaricato?{" "}
          <Link to="/agenzia/login" className="text-indigo-700 font-semibold underline hover:text-indigo-900" data-testid="user-switch-agenzia-link">
            Vai al pannello Professionista incaricato
          </Link>
        </p>
      </div>
    </div>
  );
}
